import React, { createContext, useCallback, useState, useContext } from "react";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import {
  ref,
  push as firebasePush,
  set,
  get,
  child,
  remove,
} from "firebase/database";
import { database } from "../config/firebase";
import { StudentProps } from "./student";

export interface ClassProps {
  name: string;
  description: string;
  size: number;
  begin: any;
  end: any;
  tumblr: string;
}

export interface DataTypeAluno {
  key: string;
  uid: string;
  email: string;
  name: string;
}
interface ClassContextData {
  students: ClassProps[];
  loadingClass: boolean;
  getAll(userUID: string): Promise<void>;
  createClass(studentData: ClassProps): Promise<void>;
  getClasses(): Promise<ClassProps[]>;
  deleteClass(uid: string): Promise<void>;
  deleteStudent(uidStudent: string, uidClass: string): Promise<void>;
  getAlunos(uid: string): Promise<DataTypeAluno[]>;
}

const ClassContext = createContext<ClassContextData>({} as ClassContextData);

const ClassProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const [studentsData, setStudents] = useState([] as ClassProps[]);
  const [loadingClass, setLoadingStudent] = useState(false);

  const deleteClass = useCallback(async (uid: string) => {
    try {
      await remove(child(ref(database), `classes/${uid}`));
    } catch (error) {
      throw new Error("Houve um erro inesperado");
    }
  }, []);

  const deleteStudent = useCallback(
    async (uidStudent: string, uidClass: string) => {
      await remove(
        child(ref(database), `classes/${uidClass}/students/${uidStudent}`)
      );
      await remove(
        child(ref(database), `students_classes/${uidStudent}/${uidClass}`)
      );
    },
    []
  );

  const getAlunos = useCallback(async (uid: string) => {
    try {
      const studentsSnapShot = await get(child(ref(database), "users"));

      const students: StudentProps[] = [];

      studentsSnapShot.forEach((currentSnapShot) => {
        students.push(currentSnapShot.val());
      });

      const studentsUID: string[] = [];

      const data = await get(
        child(ref(database), `classes/${uid}/students`)
      ).then(async (snapshot) => {
        if (snapshot.exists()) {
          snapshot.forEach((current) => {
            studentsUID.push(current.val().uid);
          });
        }
        const alunos = studentsUID.map((currentUID) => {
          const qualquerNome = students.find((currentFind) =>
            currentUID.includes(currentFind.uid as string)
          );
          return qualquerNome;
        });
        return alunos;
      });

      return data as DataTypeAluno[];
    } catch (error) {
      throw new Error("Houve um erro ao Carregar a Turma");
    }
  }, []);

  const getClasses = useCallback(async () => {
    try {
      const res = await get(child(ref(database), "/classes"));
      const classesList = [] as ClassProps[];
      res.forEach((current) => {
        classesList.push(current.val());
      });

      return classesList;
    } catch (error) {
      throw new Error("Houve um erro ao carregar as turmas");
    }
  }, []);

  const getAll = useCallback(async () => {
    setLoadingStudent(true);

    Swal.fire({
      title: "Carregando...",
      showConfirmButton: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: async () => {
        Swal.showLoading();
        get(child(ref(database), "/classes"))
          .then(async (snapshot) => {
            const list: any[] = [];
            snapshot.forEach((current) => {
              list.push(current.val());
            });
            setStudents(list as ClassProps[]);

            console.log(list);
            Swal.close();
          })
          .catch((err) => {
            console.log(err);
            Swal.hideLoading();
            Swal.fire({
              icon: "error",
              title: "Oops...",
              text: `Aconteceu algum erro inesperado.\nEntre em contato com o suporte técnico.`,
            });
          })
          .finally(() => {
            setLoadingStudent(false);
          });
      },
    });
  }, []);

  const createClass = useCallback(
    async ({ name, begin, end, description, size, tumblr }: ClassProps) => {
      const classRef = firebasePush(child(ref(database), "classes"));
      const uid = classRef.key;
      setLoadingStudent(true);
      console.log(name);
      Swal.fire({
        title: "Cadastrando...",
        showConfirmButton: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: async () => {
          Swal.showLoading();
          await set(classRef, {
            uid,
            name,
            begin,
            end,
            description,
            size,
            tumblr,
          })
            .then(() => {
              Swal.close();
              Swal.fire({
                icon: "success",
                title: "Cadastro de Turma",
                text: "Turma cadastrada com sucesso!",
                showConfirmButton: false,
                timer: 1800,
              }).then(() => navigate("/classes"));
            })
            .catch((err) => {
              console.log(err);
              Swal.fire({
                icon: "error",
                title: "Oops...",
                text: "Aconteceu algum erro inesperado.\nEntre em contato com o suporte técnico.",
              });
            })
            .finally(() => setLoadingStudent(false));
        },
      });
    },
    [navigate]
  );

  const providerValue = React.useMemo(
    () => ({
      getAll,
      deleteStudent,
      getAlunos,
      deleteClass,
      createClass,
      getClasses,
      students: studentsData,
      loadingClass,
    }),
    [
      getAll,
      deleteStudent,
      getAlunos,
      deleteClass,
      createClass,
      getClasses,
      studentsData,
      loadingClass,
    ]
  );

  return (
    <ClassContext.Provider value={providerValue}>
      {children}
    </ClassContext.Provider>
  );
};

function useClass(): ClassContextData {
  const context = useContext(ClassContext);

  if (!context) {
    throw new Error("useClass must be used within an AuthProvider");
  }

  return context;
}

export { ClassProvider, useClass };
